// Lógica pura do dashboard CBO (Classificação Brasileira de Ocupações).
// buildRequestBody: estado da tela -> corpo camelCase dos endpoints do BFF.
// build*VM: DTOs de dashboard do BFF -> view-models dos gráficos (barras).

import { GRAU_EXPOSICAO_RISCO_NOMES } from '@/constants/grau-exposicao-risco/GrauExposicaoRisco';

export interface CboTableFiltros {
  step: string[];
  status: string[];
  classification: string[];
  risk: string[];
  analysisOccupationCode: string[];
  analysisOccurrenceId: number[];
  // ---- CON-4199 ----
  occupationCode: string[]; // ocupação (xml)
  activityDescription: string[]; // descrição de atividade (xml)
  activityDescriptionRecommended: string[]; // descrição de atividade (recomendada)
  cargo: string[];
  setor: string[];
  // "Ambiente de trabalho" S-2240/S-2200-2206 — na prática guardam o CNPJ do empregador
  // do evento (ambiguidade documentada no backend), implementado como está.
  nociveAgentEnvironment: string[];
  legalAgreementEnvironment: string[];
  regimeTrabalhista: string[];
  grauInstrucao: string[];
  escolaridadeMinima: number[];
  cotaAprendizagem: boolean | null;
  orgaoClasse: number[];
  pcd: boolean | null;
  categoria: number[];
}

export interface CboState {
  cnpjList: string[]; // empregador selecionado
  busca: string; // busca da tabela (filterTerm)
  filtros: CboTableFiltros;
}

export interface CboRequestBody {
  cnpjList: string[];
  step: string[];
  status: string[];
  classification: string[];
  risk: string[];
  analysisOccupationCode: string[];
  analysisOccurrenceId: number[];
  filterTerm?: string;
  occupationCode: string[];
  activityDescription: string[];
  activityDescriptionRecommended: string[];
  cargo: string[];
  setor: string[];
  nociveAgentEnvironment: string[];
  legalAgreementEnvironment: string[];
  regimeTrabalhista: string[];
  grauInstrucao: string[];
  escolaridadeMinima: number[];
  cotaAprendizagem?: boolean;
  orgaoClasse: number[];
  pcd?: boolean;
  categoria: number[];
}

export function buildRequestBody(s: CboState): CboRequestBody {
  const term = s.busca.trim();
  return {
    cnpjList: s.cnpjList,
    step: s.filtros.step,
    status: s.filtros.status,
    classification: s.filtros.classification,
    risk: s.filtros.risk,
    analysisOccupationCode: s.filtros.analysisOccupationCode,
    analysisOccurrenceId: s.filtros.analysisOccurrenceId,
    filterTerm: term || undefined,
    occupationCode: s.filtros.occupationCode,
    activityDescription: s.filtros.activityDescription,
    activityDescriptionRecommended: s.filtros.activityDescriptionRecommended,
    cargo: s.filtros.cargo,
    setor: s.filtros.setor,
    nociveAgentEnvironment: s.filtros.nociveAgentEnvironment,
    legalAgreementEnvironment: s.filtros.legalAgreementEnvironment,
    regimeTrabalhista: s.filtros.regimeTrabalhista,
    grauInstrucao: s.filtros.grauInstrucao,
    escolaridadeMinima: s.filtros.escolaridadeMinima,
    cotaAprendizagem: s.filtros.cotaAprendizagem ?? undefined,
    orgaoClasse: s.filtros.orgaoClasse,
    pcd: s.filtros.pcd ?? undefined,
    categoria: s.filtros.categoria,
  };
}

// Options de filtro (CON-4199) — valores distintos existentes no banco (POST .../filtros).
export interface CboFiltroOpcaoItem {
  value: string;
  label: string;
}

export interface CboFiltrosOpcoes {
  ocupacao: CboFiltroOpcaoItem[];
  descricaoAtividades: string[];
  descricaoAtividadesRecomendada: string[];
  cargo: string[];
  setor: string[];
  ambienteS2240: string[];
  ambienteS2200S2206: string[];
  regimeTrabalhista: string[];
  grauInstrucao: CboFiltroOpcaoItem[];
  escolaridadeMinima: CboFiltroOpcaoItem[];
  orgaoClasse: CboFiltroOpcaoItem[];
  categoria: CboFiltroOpcaoItem[];
}

export const FILTROS_OPCOES_VAZIO: CboFiltrosOpcoes = {
  ocupacao: [],
  descricaoAtividades: [],
  descricaoAtividadesRecomendada: [],
  cargo: [],
  setor: [],
  ambienteS2240: [],
  ambienteS2200S2206: [],
  regimeTrabalhista: [],
  grauInstrucao: [],
  escolaridadeMinima: [],
  orgaoClasse: [],
  categoria: [],
};

// ---- Rótulos PT-BR dos enums (espelham os enums Java) ----

export const STEP_LABEL: Record<string, string> = {
  ANALISAR: 'Analisar',
  PENDENTE_DE_ESCLARECIMENTO: 'Esclarecimento',
  EM_ANALISE: 'Em análise',
  APTO_PARA_REVISAO: 'Apto para revisão',
  REVISADO: 'Revisado',
  ANALISADO: 'Analisado',
};

export const STATUS_LABEL: Record<string, string> = {
  PENDENTE_DE_ESCLARECIMENTO: 'Esclarecimento',
  EM_ANALISE: 'Em análise',
  ANALISADO: 'Analisado',
};

export const CLASSIFICATION_LABEL: Record<string, string> = {
  PENDENTE_DE_ESCLARECIMENTO: 'Esclarecimento',
  EM_ANALISE: 'Em análise',
  RISCO_DETECTADO: 'Risco detectado',
  INEXPRESSIVO: 'Inexpressivo',
};

export const RISK_LABEL: Record<string, string> = {
  INEXPRESSIVO: 'Inexpressivo',
  BAIXO: 'Baixo',
  MODERADO: 'Moderado',
  ELEVADO: 'Elevado',
  CRITICO: 'Crítico',
  EM_ANALISE: 'Em análise',
};

// ---- CON-4346 ----
export const SITUACAO_CONTRATUAL_LABEL: Record<string, string> = {
  ACTIVE: 'Ativo',
  TERMINATED: 'Inativo',
  RETIRED: 'Aposentado',
  VACATION: 'Férias',
  CEDED: 'Cedido',
};

export const PCD_LABEL: Record<string, string> = {
  true: 'Sim',
  false: 'Não',
};

export const GENERO_LABEL: Record<string, string> = {
  M: 'Masculino',
  F: 'Feminino',
};

// tpAdmissao do eSocial (S-2200) — mesmo texto do legado (CollaboratorAdmissionType)
export const TIPO_ADMISSAO_LABEL: Record<string, string> = {
  '1': '1 - Admissão',
  '2': '2 - Transferência de empresa do mesmo grupo econômico ou transferência entre órgãos do mesmo Ente Federativo',
  '3': '3 - Transferência de empresa consorciada ou de consórcio',
  '4': '4 - Transferência por motivo de sucessão, incorporação, cisão ou fusão',
  '5': '5 - Transferência do empregado doméstico para outro representante da mesma unidade familiar',
  '6': '6 - Mudança de CPF',
  '7': '7 - Transferência quando a empresa sucedida é considerada inapta por inexistência de fato',
};

// paletas por gráfico
const COR_STEP: Record<string, string> = {
  ANALISAR: '#868E96',
  PENDENTE_DE_ESCLARECIMENTO: '#E4C23F',
  EM_ANALISE: '#4263EB',
  APTO_PARA_REVISAO: '#12B886',
  REVISADO: '#7048E8',
  ANALISADO: '#2F9E44',
};

const COR_CLASSIFICATION: Record<string, string> = {
  PENDENTE_DE_ESCLARECIMENTO: '#E4C23F',
  EM_ANALISE: '#4263EB',
  RISCO_DETECTADO: '#E03131',
  INEXPRESSIVO: '#868E96',
};

const COR_STATUS: Record<string, string> = {
  PENDENTE_DE_ESCLARECIMENTO: '#E4C23F',
  EM_ANALISE: '#4263EB',
  ANALISADO: '#2F9E44',
};

export const COR_RISK: Record<string, string> = {
  CRITICO: '#C92A2A',
  ELEVADO: '#E8590C',
  MODERADO: '#E4C23F',
  BAIXO: '#2F9E44',
  INEXPRESSIVO: '#868E96',
  EM_ANALISE: '#4263EB',
};

// ---- DTOs (espelham os responses do BFF) ----

export interface StepDashboardDTO {
  analisar: number;
  analisarPercentage: number;
  pendenteEsclarecimento: number;
  pendenteEsclarecimentoPercentage: number;
  emAnalise: number;
  emAnalisePercentage: number;
  aptoRevisao: number;
  aptoRevisaoPercentage: number;
  revisado: number;
  revisadoPercentage: number;
  analisado: number;
  analisadoPercentage: number;
  total: number;
}

export interface ClassificationDashboardDTO {
  classification: Record<string, number>;
}

export interface StatusDashboardDTO {
  pendenteEsclarecimento: number;
  emAnalise: number;
  analisado: number;
  total: number;
}

export interface RiskDashboardDTO {
  totalItems: number;
  risks: { label: string; value: number; percentage: number }[];
}

export interface Barra {
  key: string; // enum de origem (estável; usado em data-qa)
  label: string;
  count: number;
  valor: number; // percentual 0-100
  cor?: string;
}

const pct = (n: number, total: number) => (total ? (n / total) * 100 : 0);

export function buildStepVM(d?: StepDashboardDTO): Barra[] {
  const dto = d ?? ({} as Partial<StepDashboardDTO>);
  const rows: [string, number, number][] = [
    ['ANALISAR', dto.analisar ?? 0, dto.analisarPercentage ?? 0],
    [
      'PENDENTE_DE_ESCLARECIMENTO',
      dto.pendenteEsclarecimento ?? 0,
      dto.pendenteEsclarecimentoPercentage ?? 0,
    ],
    ['EM_ANALISE', dto.emAnalise ?? 0, dto.emAnalisePercentage ?? 0],
    ['APTO_PARA_REVISAO', dto.aptoRevisao ?? 0, dto.aptoRevisaoPercentage ?? 0],
    ['REVISADO', dto.revisado ?? 0, dto.revisadoPercentage ?? 0],
    ['ANALISADO', dto.analisado ?? 0, dto.analisadoPercentage ?? 0],
  ];
  return rows.map(([key, count, valor]) => ({
    key,
    label: STEP_LABEL[key],
    count,
    valor,
    cor: COR_STEP[key],
  }));
}

// Gráfico de status (perfil cliente/usuário) — 3 status derivados. Espelha
// o gráfico "Status de análise" do legado (mostrado quando NÃO é analista).
export function buildStatusVM(d?: StatusDashboardDTO): Barra[] {
  const dto = d ?? ({} as Partial<StatusDashboardDTO>);
  const rows: [string, number][] = [
    ['PENDENTE_DE_ESCLARECIMENTO', dto.pendenteEsclarecimento ?? 0],
    ['EM_ANALISE', dto.emAnalise ?? 0],
    ['ANALISADO', dto.analisado ?? 0],
  ];
  const total = dto.total ?? rows.reduce((acc, [, n]) => acc + n, 0);
  return rows.map(([key, count]) => ({
    key,
    label: STATUS_LABEL[key],
    count,
    valor: pct(count, total),
    cor: COR_STATUS[key],
  }));
}

export function buildClassificationVM(d?: ClassificationDashboardDTO): Barra[] {
  const map = d?.classification ?? {};
  const keys = ['PENDENTE_DE_ESCLARECIMENTO', 'EM_ANALISE', 'RISCO_DETECTADO', 'INEXPRESSIVO'];
  const total = keys.reduce((acc, k) => acc + (map[k] ?? 0), 0);
  return keys.map((k) => ({
    key: k,
    label: CLASSIFICATION_LABEL[k],
    count: map[k] ?? 0,
    valor: pct(map[k] ?? 0, total),
    cor: COR_CLASSIFICATION[k],
  }));
}

export function buildRiskVM(d?: RiskDashboardDTO): Barra[] {
  // CON-5406: só graus de risco válidos no gráfico. "Em análise" é etapa, não grau —
  // não consta no enum canônico GrauExposicaoRisco, então é descartado por whitelist.
  return (d?.risks ?? [])
    .filter((r) => r.label in GRAU_EXPOSICAO_RISCO_NOMES)
    .map((r) => ({
      key: r.label,
      label: RISK_LABEL[r.label] ?? r.label,
      count: r.value,
      valor: r.percentage,
      cor: COR_RISK[r.label],
    }));
}
